import { useEffect, useRef } from 'react';

/** Minimum gap between background refreshes while the user keeps editing in another tab. */
const DEFAULT_MIN_INTERVAL_MS = 60000;
/** Short floor so a burst of messages is coalesced into one refresh. */
const COALESCE_MS = 1000;

/**
 * Listen for editor-tab messages (same origin only) and call `onUpdates` with the latest
 * payload per document. Editor tabs autosave and post DOCUMENT_UPDATED after every save, and
 * the opener's refresh can be expensive (a full project reload), so refreshes are rate-limited:
 *  - immediately when the editor tab closes (DOCUMENT_EDITOR_CLOSE), when this tab becomes
 *    visible again or when this window gets focus (the user is back on the list);
 *  - otherwise at most once per `minInterval` while updates keep arriving (side-by-side
 *    windows), and never while this tab is hidden.
 * This replaces the old DOCUMENT_EDITOR_SAVE flow, where the opener wrote the same content
 * to the database a second time.
 *
 * @param {(updates: Array<{documentId: string, updatedAt: string|null, content?: string, title?: string}>) => void} onUpdates - Refresh handler.
 * @param {{minInterval?: number}} [options] - Background refresh throttle in ms (default 60000).
 * @returns {void}
 */
export default function useDocumentUpdatedListener(onUpdates, { minInterval = DEFAULT_MIN_INTERVAL_MS } = {}) {
  const handlerRef = useRef(onUpdates);
  handlerRef.current = onUpdates;

  useEffect(() => {
    const pending = new Map();
    let timer = null;
    let lastFlushAt = 0;

    const flush = () => {
      clearTimeout(timer);
      timer = null;
      if (!pending.size || document.visibilityState === 'hidden') return;
      const updates = Array.from(pending.values());
      pending.clear();
      lastFlushAt = Date.now();
      try {
        handlerRef.current?.(updates);
      } catch (error) {
        console.error('Document refresh failed:', error);
      }
    };

    const schedule = () => {
      if (timer) return;
      const wait = Math.max(COALESCE_MS, lastFlushAt + minInterval - Date.now());
      timer = setTimeout(flush, wait);
    };

    const record = (data) => {
      const previous = pending.get(data.documentId) || {};
      pending.set(data.documentId, {
        documentId: data.documentId,
        updatedAt: data.updatedAt || previous.updatedAt || null,
        content: data.content !== undefined ? data.content : previous.content,
        title: data.title !== undefined ? data.title : previous.title,
      });
    };

    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const type = event.data?.type;
      const data = event.data?.data || {};
      if (!data.documentId) return;
      if (type === 'DOCUMENT_EDITOR_CLOSE') {
        // The editor saved before closing; refresh now (or as soon as this tab is visible).
        record(data);
        flush();
        return;
      }
      // DOCUMENT_EDITOR_SAVE is sent by editor tabs opened before this release; they already
      // saved to the database themselves, so only refresh.
      if (type !== 'DOCUMENT_UPDATED' && type !== 'DOCUMENT_EDITOR_SAVE') return;
      record(data);
      schedule();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') flush();
    };
    const onFocus = () => flush();

    window.addEventListener('message', onMessage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [minInterval]);
}
